import PropTypes from 'prop-types';
import React from 'react';
import StyledSystemPropTypes from '@styled-system/prop-types';

import { ButtonElement } from '@components/button/button-element';
import { useBrazeImpression } from '@hooks/use-braze-impression';
import { Button } from '@components/button/new';
import { capitalize } from 'lodash';
import { Box, Flex } from '../../grid';
import { Image } from '../../image';
import { ModalPattern } from '../../decal/modal-pattern';
import { H6, P } from '../../typography/new';
import { TextClamp } from '../../text-clamp';

const PROMO_CARD_NUMBER_HEIGHT_BREAKPOINTS = {
  _: 200,
  md: 240,
};
export const PROMO_CARD_HEIGHT_BREAKPOINTS = {
  _: `${PROMO_CARD_NUMBER_HEIGHT_BREAKPOINTS._}px`,
  md: `${PROMO_CARD_NUMBER_HEIGHT_BREAKPOINTS.md}px`,
  lg: `${PROMO_CARD_NUMBER_HEIGHT_BREAKPOINTS.lg}px`,
};
const ASPECT_RATIO = 7 / 4;
const PROMO_CARD_WIDTH_BREAKPOINTS = {
  _: `${PROMO_CARD_NUMBER_HEIGHT_BREAKPOINTS._ * ASPECT_RATIO}px`,
  md: `${PROMO_CARD_NUMBER_HEIGHT_BREAKPOINTS.md * ASPECT_RATIO}px`,
  lg: `${PROMO_CARD_NUMBER_HEIGHT_BREAKPOINTS.lg * ASPECT_RATIO}px`,
};

const backgroundChips = '/static/images/promotions/regchips.jpg';

const BrazePromotionCard = ({ brazeData, aspectRatio, height, width }) => {
  useBrazeImpression({ brazeLogImpression: brazeData?.brazeLogImpression });

  const { header, description, disclaimer, buttonText, buttonLink, image, textWidth } = brazeData;

  return (
    <PromotionCard
      header={header}
      description={description}
      disclaimer={disclaimer}
      buttonText={buttonText}
      buttonLink={buttonLink}
      textWidth={textWidth}
      image={image?.[0]?.url || backgroundChips}
      onClick={() => {
        brazeData.brazeLogClick?.();
      }}
      aspectRatio={aspectRatio}
      height={height}
      width={width}
    />
  );
};

BrazePromotionCard.propTypes = {
  brazeData: PropTypes.object.isRequired,
  aspectRatio: PropTypes.string,
  height: StyledSystemPropTypes.layout.height,
  width: StyledSystemPropTypes.layout.width,
};

BrazePromotionCard.defaultProps = {
  aspectRatio: null,
  height: PROMO_CARD_HEIGHT_BREAKPOINTS,
  width: PROMO_CARD_WIDTH_BREAKPOINTS,
};

const OfferPromotionCard = ({ offer, isApplied, onClick, aspectRatio, height, width, hideButton, borderRadius }) => (
  <PromotionCard
    header={offer?.name}
    description={offer?.description}
    disclaimer={offer?.formattedExpiryDate && `Expires ${offer.formattedExpiryDate}`}
    buttonText={isApplied ? 'APPLIED' : offer?.formattedLabel}
    buttonLink={null}
    onClick={onClick}
    image={offer?.image || backgroundChips}
    aspectRatio={aspectRatio}
    height={height}
    width={width}
    hideButton={hideButton}
    borderRadius={borderRadius}
  />
);

OfferPromotionCard.propTypes = {
  offer: PropTypes.object.isRequired,
  isApplied: PropTypes.bool,
  onClick: PropTypes.func,
  aspectRatio: PropTypes.string,
  height: StyledSystemPropTypes.layout.height,
  width: StyledSystemPropTypes.layout.width,
  hideButton: PropTypes.bool,
  borderRadius: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
};

OfferPromotionCard.defaultProps = {
  isApplied: false,
  onClick: null,
  aspectRatio: null,
  height: PROMO_CARD_HEIGHT_BREAKPOINTS,
  width: PROMO_CARD_WIDTH_BREAKPOINTS,
  hideButton: false,
  borderRadius: undefined,
};

const PromotionCard = ({
  header,
  description,
  disclaimer,
  image,
  buttonText,
  buttonLink,
  onClick,
  role,
  aspectRatio,
  height,
  width,
  textColor,
  hideButton,
  borderRadius,
}) => {
  return (
    <Box
      role={role}
      flexShrink={0}
      style={{ aspectRatio }}
      height={height}
      width={width}
      position="relative"
      borderRadius={borderRadius}
      overflow="hidden"
      backgroundColor="warmWhite3"
    >
      <ButtonElement style={{ display: 'flex', width: '100%', height: '100%' }} onClick={onClick} href={buttonLink}>
        <Flex
          flexDirection="column"
          alignItems="flex-start"
          justifyContent="space-between"
          height="100%"
          textAlign="left"
          width="62%"
        >
          <Flex
            flexDirection="column"
            alignItems="flex-start"
            justifyContent="center"
            zIndex={2}
            height="100%"
            width="100%"
            maxWidth="100%"
            flex={1}
            color={textColor}
            pt="20px"
            px="20px"
          >
            <H6 hideTramlines my="0" color="inherit" zIndex={2}>
              {header}
            </H6>

            <P variant="small" mt="6px" color="inherit">
              <TextClamp lines={3}>{description}</TextClamp>
            </P>

            {disclaimer && (
              <P variant="extra-small" mt={description ? '6px' : '2px'} color="grey70">
                {disclaimer}
              </P>
            )}
            {(!!onClick || buttonText) && !hideButton ? (
              <Box>
                <Button mt="12px" size="small" widthType="fit" variant="secondary" data-testid="offer-card-cta">
                  {capitalize(buttonText)}
                </Button>
              </Box>
            ) : null}
          </Flex>
          <Flex>
            <ModalPattern />
          </Flex>
        </Flex>
        <Flex width="38%" height="100%" marginLeft="auto">
          <Image width="100%" height="100%" src={image} focalPoint={[1, 0.5]} objectFit="cover" />
        </Flex>
      </ButtonElement>
    </Box>
  );
};

PromotionCard.propTypes = {
  onClick: PropTypes.func,
  header: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  disclaimer: PropTypes.string,
  image: PropTypes.string.isRequired,
  buttonText: PropTypes.string,
  buttonLink: PropTypes.string,
  role: PropTypes.string,
  aspectRatio: PropTypes.string,
  height: StyledSystemPropTypes.layout.height,
  width: StyledSystemPropTypes.layout.width,
  textColor: PropTypes.string,
  /**
    The card size is set quite explicitly, always adhering to the same aspect ratio
    This is to make it easier for Nando's to enter a background image, and predict how the card content will behave with it
  */
  hideButton: PropTypes.bool,
  borderRadius: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
};

PromotionCard.defaultProps = {
  onClick: null,
  disclaimer: null,
  buttonText: null,
  buttonLink: null,
  role: null,
  aspectRatio: null,
  height: PROMO_CARD_HEIGHT_BREAKPOINTS,
  width: PROMO_CARD_WIDTH_BREAKPOINTS,
  textColor: 'black',
  hideButton: false,
  borderRadius: { xs: '24px', lg: '40px' },
};

export { PromotionCard, BrazePromotionCard, OfferPromotionCard };
